import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { FirmwareUpdateModalProps } from '@/app-shell/chronicle/device-settings/firmware-update-modal';
import { ModalPriority } from '@/app-shell/modal-queue.types';
import {
  chronicleDeviceStateAtom,
  FIRMWARE_MODAL_ID,
  firmwareModalUserOpenAtom,
  firmwareStartupSeenAtom,
  pendingLocalFirmwareFileAtom,
} from '@/atoms/chronicle';
import {
  currentQueuedModalAtom,
  dequeueModalAtom,
  enqueueModalAtom,
  recordPacedLaunchModalDismissedAtom,
} from '@/atoms/modalQueueAtoms';
import { useChronicleDeviceStatus, useChronicleOta, useOtaGuards } from '@/hooks/desktop';
import {
  reportOtaStartMissingUrl,
  reportOtaStartRejected,
} from '@/hooks/desktop/ota-start-failure';
import {
  trackNativeFirmwareDownloadComplete,
  trackNativeFirmwareDownloadFailed,
  trackNativeFirmwareDownloadStart,
  trackNativeFirmwareUpdateAvailable,
} from '@/utils/analytics/track-hardware-events';
import {
  trackFirmwareUpgradeFailed,
  trackFirmwareUpgradeStarted,
} from '@/utils/analytics/track-ota-events';
import { extractDeviceModel } from '@/utils/chronicle/device-helpers';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';

const OTA_LOG_SOURCE = 'useFirmwareUpdateFlow';

type TerminalOtaStatus = Extract<
  FirmwareUpdateModalProps['otaStatus'],
  'completed' | 'failed'
>;

export interface UseFirmwareUpdateFlowReturn {
  // Modal state
  isUpdateModalOpen: boolean;
  closeUpdateModal: () => void;

  // Actions
  handleUpgrade: () => void;

  // Props to spread directly onto FirmwareUpdateModal
  modalProps: FirmwareUpdateModalProps;
}

/**
 * Orchestrates the firmware update flow for the single globally-mounted modal instance
 * in GlobalModalsWrapper.
 *
 * Two ways in, same modal UX (confirm → in-progress → completed):
 * 1. Banner click → `firmwareModalUserOpenAtom`, opening the modal outright. A click is
 *    a direct response, so it is not arbitrated by the modal queue — competing there let
 *    any equal-or-higher launch modal win the sort and swallow the click silently.
 * 2. Startup check → the modal queue, which is the right place for an app-chosen interrupt.
 */
export function useFirmwareUpdateFlow(): UseFirmwareUpdateFlowReturn {
  const intl = useIntl();
  const { t } = useTranslations();
  const [latchedTerminalOtaStatus, setLatchedTerminalOtaStatus] =
    useState<TerminalOtaStatus | null>(null);

  const {
    deviceId,
    deviceName,
    firmwareVersion,
    batteryLevel,
    latestFirmwareVersion,
    latestFirmwareDownloadUrl,
    firmwareWhatsNew,
    firmwareUpdateStatus,
    firmwareUpdateProgress,
    firmwareUpdateError,
    firmwareUpdateType,
  } = useChronicleDeviceStatus();

  const { startUpdate } = useChronicleOta();

  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const enqueueModal = useSetAtom(enqueueModalAtom);
  const dequeueModal = useSetAtom(dequeueModalAtom);
  const setPendingLocalFile = useSetAtom(pendingLocalFirmwareFileAtom);
  const pendingLocalFile = useAtomValue(pendingLocalFirmwareFileAtom);
  const currentQueuedModal = useAtomValue(currentQueuedModalAtom);
  const isUserOpened = useAtomValue(firmwareModalUserOpenAtom);
  const setUserOpened = useSetAtom(firmwareModalUserOpenAtom);
  const setFirmwareStartupSeen = useSetAtom(firmwareStartupSeenAtom);
  const recordPacedShow = useSetAtom(recordPacedLaunchModalDismissedAtom);

  // A direct open bypasses queue *ordering*, not queue *exclusivity* — a hard gate
  // (forced migration, trial block) must still win, or the firmware dialog would
  // stack a second backdrop over a modal whose whole job is to block the app.
  const hardGateQueued =
    currentQueuedModal != null &&
    currentQueuedModal.id !== FIRMWARE_MODAL_ID &&
    (!currentQueuedModal.dismissible ||
      currentQueuedModal.priority >= ModalPriority.CRITICAL);

  const isUpdateModalOpen =
    (isUserOpened && !hardGateQueued) || currentQueuedModal?.id === FIRMWARE_MODAL_ID;
  const isLocalFileFlow = pendingLocalFile !== null;

  const upgradeBlockedReason = useOtaGuards(batteryLevel);

  const resetOtaState = useCallback(() => {
    setDeviceState((prev) => ({
      ...prev,
      firmwareUpdateInProgress: false,
      firmwareUpdateStatus: null,
      firmwareUpdateProgress: null,
      firmwareUpdateError: null,
    }));
  }, [setDeviceState]);

  const closeUpdateModal = useCallback(() => {
    setUserOpened(false);
    dequeueModal(FIRMWARE_MODAL_ID);
    setPendingLocalFile(null);
  }, [setUserOpened, dequeueModal, setPendingLocalFile]);

  // Reset stale state when the modal opens (handles both remote and local file flows).
  // Using wasOpenRef ensures this runs only on the false→true transition, not on
  // every render while the modal is open.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isUpdateModalOpen && !wasOpenRef.current;
    wasOpenRef.current = isUpdateModalOpen;
    if (!justOpened) return;
    logger.info({
      msg: '[useFirmwareUpdateFlow] Firmware update modal opened',
      source: isUserOpened ? 'user' : 'startup_check',
    });
    // Pin the startup-check open to userTriggeredModals so launchModalsAtom condition
    // changes (e.g. firmwareUpdateStatus going non-null when OTA starts) don't close the
    // modal mid-flow. The direct-open flag already holds the banner path open.
    if (!isUserOpened) {
      enqueueModal({
        id: FIRMWARE_MODAL_ID,
        priority: ModalPriority.NORMAL,
        dismissible: true,
      });
      // Consume the pacing slot here: marking seen below drops the id from
      // launchModalsAtom, after which the dismiss-time writer's launch-membership
      // guard can never pass. Showing the modal is what spends the session budget.
      recordPacedShow(FIRMWARE_MODAL_ID);
      // Mark seen on show, not on close. Marking on close leaves the entry
      // queued whenever it never actually surfaced, so it springs on the user
      // later — typically right after a BLE reconnect flips
      // firmwareUpdateAvailable back to true. Must follow the pin above, which
      // is what keeps the modal open once this drops it from launchModalsAtom.
      setFirmwareStartupSeen(true);
    }
    setLatchedTerminalOtaStatus(null);
    resetOtaState();
    trackNativeFirmwareUpdateAvailable({
      currentVersion: firmwareVersion ?? '',
      availableVersion: latestFirmwareVersion ?? '',
      updateType: firmwareUpdateType === 'required' ? 'forced' : 'recommended',
      deviceModel: extractDeviceModel(deviceName) ?? '',
    });
  }, [
    isUpdateModalOpen,
    isUserOpened,
    resetOtaState,
    enqueueModal,
    recordPacedShow,
    setFirmwareStartupSeen,
    firmwareVersion,
    latestFirmwareVersion,
    firmwareUpdateType,
    deviceName,
  ]);

  // Show toast when OTA error occurs while modal is closed (edge case: error after navigating away).
  useEffect(() => {
    if (isUpdateModalOpen || !firmwareUpdateError) return;
    if (latchedTerminalOtaStatus === 'failed') return;

    toast({
      title: t('settings.chronicle.error.firmwareUpdateFailedTitle'),
      description:
        firmwareUpdateError.message ||
        t('settings.chronicle.error.firmwareUpdateFailedDescription'),
      tone: 'destructive',
    });
    resetOtaState();
  }, [
    firmwareUpdateError,
    isUpdateModalOpen,
    latchedTerminalOtaStatus,
    t,
    resetOtaState,
  ]);

  // Close modal on cancellation
  useEffect(() => {
    if (!isUpdateModalOpen) return;
    if (firmwareUpdateStatus === 'cancelled') {
      closeUpdateModal();
    }
  }, [firmwareUpdateStatus, isUpdateModalOpen, closeUpdateModal]);

  // Preserve terminal modal views while the modal is open. A successful firmware
  // update refreshes device state on reconnect, which can clear OTA status before
  // the user dismisses the completed screen.
  useEffect(() => {
    if (!isUpdateModalOpen) return;
    if (firmwareUpdateStatus === 'completed' || firmwareUpdateStatus === 'failed') {
      setLatchedTerminalOtaStatus(firmwareUpdateStatus);
    }
  }, [firmwareUpdateStatus, isUpdateModalOpen]);

  const lastOtaStatusRef = useRef(firmwareUpdateStatus);
  useEffect(() => {
    const prev = lastOtaStatusRef.current;
    const next = firmwareUpdateStatus;
    lastOtaStatusRef.current = next;
    if (prev === next) return;
    if (!isUpdateModalOpen) return;

    const version = latestFirmwareVersion ?? '';
    const model = extractDeviceModel(deviceName) ?? '';

    if (next === 'downloading') {
      trackNativeFirmwareDownloadStart({ version, deviceModel: model });
    } else if (
      prev === 'downloading' &&
      (next === 'starting' || next === 'transferring')
    ) {
      trackNativeFirmwareDownloadComplete({ version, deviceModel: model });
    } else if (prev === 'downloading' && next === 'failed') {
      trackNativeFirmwareDownloadFailed({
        version,
        deviceModel: model,
        errorType: firmwareUpdateError?.code ?? 'UNKNOWN',
        errorDescription: firmwareUpdateError?.message ?? '',
      });
    }
  }, [
    isUpdateModalOpen,
    firmwareUpdateStatus,
    latestFirmwareVersion,
    deviceName,
    firmwareUpdateError,
  ]);

  /**
   * Unified upgrade handler — called by modal "Upgrade now" button.
   * Uses local file path if one was selected via DeviceSection dev tools,
   * otherwise uses the remote download URL.
   */
  const handleUpgrade = () => {
    const isLocal = !!pendingLocalFile;
    trackFirmwareUpgradeStarted({
      deviceId,
      deviceName,
      currentFirmwareVersion: firmwareVersion,
      targetFirmwareVersion: isLocal ? null : latestFirmwareVersion,
      updateSource: isLocal ? 'local' : 'remote',
    });

    void (async () => {
      try {
        if (pendingLocalFile) {
          await startUpdate({ filePath: pendingLocalFile });
        } else {
          if (!latestFirmwareDownloadUrl) {
            reportOtaStartMissingUrl(OTA_LOG_SOURCE, intl);
            return;
          }
          await startUpdate({
            downloadUrl: latestFirmwareDownloadUrl,
            targetVersion: latestFirmwareVersion ?? '',
            deviceModel: extractDeviceModel(deviceName),
          });
        }
      } catch (err) {
        // Pre-flight rejections emit no OTA state event, so without this the
        // funnel keeps a `started` with no terminal event.
        const { errorCode, message, isFailure } = reportOtaStartRejected(
          OTA_LOG_SOURCE,
          err,
          intl
        );
        if (isFailure) {
          trackFirmwareUpgradeFailed({
            deviceId,
            deviceName,
            targetFirmwareVersion: isLocal ? null : latestFirmwareVersion,
            errorCode: errorCode ?? 'UNKNOWN',
            errorMessage: message,
            isRetryable: true,
          });
        }
      }
    })();
  };

  const modalOtaStatus = latchedTerminalOtaStatus ?? firmwareUpdateStatus;

  return {
    isUpdateModalOpen,
    closeUpdateModal,
    handleUpgrade,
    modalProps: {
      isOpen: isUpdateModalOpen,
      onClose: closeUpdateModal,
      latestVersion: latestFirmwareVersion,
      whatsNew: firmwareWhatsNew,
      otaStatus: modalOtaStatus,
      otaProgress: firmwareUpdateProgress,
      isLocalFileFlow,
      upgradeBlockedReason,
      onUpgrade: handleUpgrade,
      onRemindLater: closeUpdateModal,
    },
  };
}
